/**
 * Copyright 2022-2025 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { ApolloError } from "@apollo/client/errors";
import {
  ResourceFragment,
  ResourcesSearchInput,
  ResourcesSortInput,
  useResourcesSearchQuery,
  useResourcesSearchBasicQuery,
} from "~/generated/graphql";
import { isEmpty } from "~/tools";

type BaseProps = {
  page: string;
  limit: string;
  sort?: ResourcesSortInput;
  input: ResourcesSearchInput;
  includeAccount?: boolean;
  includeGroupIdentifiers?: boolean;
  includeFuturePatchingActions?: boolean;
};

type SearchResult<T> = {
  data: T[];
  loading: boolean;
  error: ApolloError | undefined;
  count: number;
  noData: boolean;
};

type BasicSearchResult = SearchResult<{
  __typename?: "Resource";
  id: string;
  name: string;
}>;
type FullSearchResult = SearchResult<ResourceFragment>;

export function useResourcesSearch(
  props: BaseProps & { basic: true }
): BasicSearchResult;
export function useResourcesSearch(
  props: BaseProps & { basic?: false | undefined }
): FullSearchResult;
export function useResourcesSearch({
  page,
  limit,
  sort,
  input,
  includeAccount,
  includeGroupIdentifiers,
  includeFuturePatchingActions,
  basic,
}: BaseProps & { basic?: boolean }) {
  const variables = {
    page: Number(page),
    limit: Number(limit),
    sort,
    input,
    includeAccount,
    includeGroupIdentifiers,
    includeFuturePatchingActions,
  } as const;
  const basicResult = useResourcesSearchBasicQuery({ variables, skip: !basic });
  const fullResult = useResourcesSearchQuery({ variables, skip: !!basic });
  const {
    data: resourcesData,
    loading,
    error,
  } = basic ? basicResult : fullResult;

  const data = resourcesData?.resourcesSearch?.result ?? [];
  const count = resourcesData?.resourcesSearch?.pageInfo?.count ?? 0;
  const noData = isEmpty(data);

  return {
    data,
    loading,
    error,
    count,
    noData,
  } as const;
}
